import type { AppUpdate } from 'src/types/app-updates/app-update';

import { useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMegaphone } from '@fortawesome/pro-regular-svg-icons';

import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import { useLikeToggle } from 'src/hooks/app-updates/use-like-toggle';

import { fTimeAgo } from 'src/utils/format-time';
import { getLocalizedText } from 'src/utils/multilingual';

import { useTranslate } from 'src/locales';

import { Label } from 'src/components/label';
import { toast } from 'src/components/snackbar';
import { AnimatedLikeButton } from 'src/components/animate/animated-like-button';

type AppUpdateItemProps = {
  update: AppUpdate;
  onClick: (id: number) => void;
};

export function AppUpdateItem({ update, onClick }: AppUpdateItemProps) {
  const { t, currentLang } = useTranslate('app-updates');
  const lang = currentLang.value;

  const { liked, likeCount, isLiking, toggle } = useLikeToggle({
    updateId: update.id,
    initialLiked: update.liked,
    initialLikeCount: update.likeCount,
    onError: () => toast.error(t('errors.like')),
  });

  const handleLikeClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      toggle();
    },
    [toggle]
  );

  const handleItemClick = useCallback(() => {
    onClick(update.id);
  }, [onClick, update.id]);

  return (
    <MenuItem
      onClick={handleItemClick}
      sx={{
        py: 1,
        px: 1.5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        whiteSpace: 'normal',
        borderRadius: 0,
        borderBottom: (theme) => `1px solid ${theme.vars.palette.divider}`,
        '&:last-child': {
          borderBottom: 'none',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: 1 }}>
        <FontAwesomeIcon icon={faMegaphone} size="lg" style={{ flexShrink: 0 }} />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {getLocalizedText(update.title, lang)}
        </Typography>
        {!update.seen && <Label color="success">{t('new')}</Label>}
      </Box>

      <Typography
        variant="body2"
        sx={(theme) => ({
          color: 'text.secondary',
          ...theme.mixins.maxLine({ line: 2 }),
        })}
      >
        {getLocalizedText(update.body, lang)}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: 1,
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {fTimeAgo(update.publishedDate)}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AnimatedLikeButton liked={liked} isLiking={isLiking} onClick={handleLikeClick} />
          {likeCount > 0 && (
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {likeCount}
            </Typography>
          )}
        </Box>
      </Box>
    </MenuItem>
  );
}
